/**
 * Breadcrumbs — URL-derived breadcrumb trail.
 *
 * Labels are DERIVED from the generated portal registry (no hand-maintained
 * name map): exact path matches use the registry label; unknown segments are
 * prettified (kebab → Title Case); UUIDs and long numeric ids are shortened.
 */

import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Breadcrumbs as MuiBreadcrumbs, Link, Typography } from '@mui/material';
import { labelForPath, MODULE_CONFIGS, type ModuleId } from '../registry/portalRegistry';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function prettify(segment: string): string {
  if (UUID_RE.test(segment)) return `${segment.slice(0, 8)}…`;
  if (/^\d{6,}$/.test(segment)) return `#${segment.slice(0, 6)}…`;
  return segment
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  const crumbs = segments.map((segment, i) => {
    const path = '/' + segments.slice(0, i + 1).join('/');
    // A module root crumb reads as the module name, not its nav item label.
    const moduleLabel = i === 0 ? MODULE_CONFIGS[segment as ModuleId]?.label : undefined;
    return {
      path,
      label: moduleLabel ?? labelForPath(path) ?? prettify(segment),
      isLast: i === segments.length - 1,
    };
  });

  return (
    <MuiBreadcrumbs sx={{ mb: 2, fontSize: '0.8rem' }} aria-label="breadcrumb">
      <Link component={RouterLink} to="/" underline="hover" color="inherit" sx={{ fontSize: '0.8rem' }}>
        Home
      </Link>
      {crumbs.map((crumb) =>
        crumb.isLast ? (
          <Typography key={crumb.path} color="text.primary" sx={{ fontSize: '0.8rem' }}>
            {crumb.label}
          </Typography>
        ) : (
          <Link
            key={crumb.path}
            component={RouterLink}
            to={crumb.path}
            underline="hover"
            color="inherit"
            sx={{ fontSize: '0.8rem' }}
          >
            {crumb.label}
          </Link>
        ),
      )}
    </MuiBreadcrumbs>
  );
};

export default Breadcrumbs;
